import { useCallback } from 'react';
import { Path, useFormContext } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import type { HotelPensionPricingFormData } from '@/types/benefits/hotel/HotelPensionPricingSchema';
import { parsePensionTariffBlock } from '@/utils/benefits/hotel/excelPaste/parsePensionTariffBlock';
import ExcelPastePanel from './ExcelPastePanel';
import { ChildAges } from './pasteFormHelpers';
import {
  buildPensionRecap,
  getPastedPensionFieldNames,
  mergePastedPensionCard,
} from './pensionPasteHelpers';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';

interface PensionTariffPasteProps {
  /** `commonPensionPricings` or `hotelRoomCategories.n.categoryPensionPricings`. */
  fieldArrayName: string;
  cardIndex: number;
  childBandsCount: ChildBandsCount;
  childAges: ChildAges;
}

const setOptions = { shouldDirty: true, shouldValidate: true };

/** Paste of a 12×3 pension block into one pension tariff card. */
const PensionTariffPaste = ({
  fieldArrayName,
  cardIndex,
  childBandsCount,
  childAges,
}: PensionTariffPasteProps) => {
  const { t } = useTranslation();
  const { getValues, setValue } =
    useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const { formatIssue } = useExcelPasteFormatters(childAges);

  const cardPrefix = `${fieldArrayName}.${cardIndex}`;
  const tariffName = useCallback(
    (name: string | null | undefined, index: number) =>
      name || t('tariffs.tarif_', { index: index + 1 }),
    [t],
  );

  const parse = useCallback(
    (text: string) => {
      const cards = (getValues(
        fieldArrayName as Path<HotelFormDataWithTranslations>,
      ) ?? []) as HotelPensionPricingFormData[];
      return {
        cards,
        result: parsePensionTariffBlock(text, {
          childBandsCount,
          basePensionType: getValues('pricingPensionType')?.value as
            | string
            | undefined,
          otherTariffs: cards
            .map((card, index) => ({
              name: tariffName(card.name, index),
              periods: card.periods ?? [],
            }))
            .filter((_, index) => index !== cardIndex),
        }),
      };
    },
    [cardIndex, childBandsCount, fieldArrayName, getValues, tariffName],
  );

  const analyze = useCallback(
    (text: string) => {
      const { cards, result } = parse(text);
      if (!result.value) {
        return {
          errors: result.errors.map(formatIssue),
          warnings: [],
          recap: [],
        };
      }
      return {
        errors: [],
        warnings: result.warnings.map(formatIssue),
        recap: buildPensionRecap(
          cards[cardIndex],
          result.value,
          {
            basePensionType: getValues('pricingPensionType')?.value as
              | string
              | undefined,
            childBandsCount,
            childAges,
          },
          t,
        ),
      };
    },
    [cardIndex, childAges, childBandsCount, formatIssue, getValues, parse, t],
  );

  const apply = useCallback(
    (text: string) => {
      const { cards, result } = parse(text);
      const pasted = result.value;
      if (!pasted) return;

      const card = mergePastedPensionCard(cards[cardIndex], pasted);
      const set = (name: string, value: unknown) =>
        setValue(
          name as Path<HotelFormDataWithTranslations>,
          value as never,
          setOptions,
        );
      set(`${cardPrefix}.name`, card.name);
      set(`${cardPrefix}.periods`, card.periods);
      set(`${cardPrefix}.prices`, card.prices);

      markPasted(getPastedPensionFieldNames(cardPrefix, card, pasted));
    },
    [cardIndex, cardPrefix, markPasted, parse, setValue],
  );

  return <ExcelPastePanel shape="pension" analyze={analyze} apply={apply} />;
};

export default PensionTariffPaste;
